import * as React from "react";
import { PoweredByFooter } from "./powered-by-footer";
import {MaybeError} from "@components/maybe-error";
import {If} from "@components/if";
import { getHeaderClass } from "./get-header-class";

type SearchType = "email" | "order";

interface Props {
    themeColor: string
    shopUrl: string
    allowSearchViaOrderNumber: boolean;
    error: string | null
    searchType: SearchType
    searchTerm: string
    onSetSearchType: (value: SearchType) => void
    onSetSearchTerm: (value: string) => void
    onBeginSearch: () => void;
}

interface Strings {
    header: string
    description: {
        email: string
        order: string
    }
    searchTypeSelector: {
        email: string
        order: string
    }
    searchButton: {
        beginSearch: string
    }
}

const STRINGS: Record<"EN" | "ES", Strings> = {
    EN: {
        header: "Look up the status of your orders.",
        description: {
            email: "Enter the email address you used to place your order and we'll look up its current status. Your email address will not be shared.",
            order: "Enter your order number and we'll look up its current status."
        },
        searchTypeSelector: {
            email: "Search by email address",
            order: "Search by order number"
        },
        searchButton: {
            beginSearch: "Find my order!"
        }
    },
    ES: {
        header: "Mira acá como va avanzando tu pedido",
        description: {
            email: "Ingresa acá el mail con el que hiciste tu compra.\n\nTe aparecerá el o los pedidos que has realizado. Haz \"click\" en el pedido que necesitas revisar.\n\nAhí podrás ver el avance de tu compra, hasta que está listo para retiro o envío.\n\nSi no hay información, es porque es muy pronto. Puede tardar hasta 3 días hábiles en actualizarse.\n\nNo te preocupes, porque de todas maneras, estamos avanzando en tu pedido y te cumpliremos con el plazo de 5 a 10 días hábiles en producción. Siempre el primer día hábil, es el siguiente a la compra. Luego viene el plazo de envío, el cual está indicado en tu pedido.",
            order: "Ingresa acá el numero del tu pedido.\n\nTe aparecerá el has realizado. Haz \"click\" en el pedido que necesitas revisar."
        },
        searchTypeSelector: {
            email: "Ingresa por el mail del tu pedido",
            order: "Ingresa por el numero del tu pedido"
        },
        searchButton: {
            beginSearch: "Buscar el estado de mi pedido"
        }
    }
}

export function SearchSection(props: Props): JSX.Element {
    // Oficial - Spanish spelling
    const strings = props.shopUrl === "marca2-oficial.myshopify.com" ? STRINGS.ES : STRINGS.EN;
    
    function beginSearch() {
        props.onBeginSearch();
    }

    function handleSetSearchTerm(e: React.FormEvent<HTMLInputElement>) {
        props.onSetSearchTerm(e.currentTarget.value);
    }

    function handleSetSearchType(e: React.FormEvent<HTMLSelectElement>) {
        props.onSetSearchType(e.currentTarget.value === "email" ? "email" : "order");
    }

    const widgetDescription = props.searchType === "email"
        ? strings.description.email
        : strings.description.order
    const headerClass = getHeaderClass(props.shopUrl);

    return (
        <section className="m-col-18-24">
            <div className="box" style={{ borderTop: "6px solid " + props.themeColor }}>
                <form className="panel active" onSubmit={beginSearch}>
                    <div className={headerClass}>
                        <h4>{strings.header}</h4>
                        <p>
                            {widgetDescription}
                        </p>
                    </div>
                    <div className="body">
                        <div className="form-group">
                            <If condition={props.allowSearchViaOrderNumber}>
                                <select value={props.searchType} onChange={handleSetSearchType}>
                                    <option value={"email"}>{strings.searchTypeSelector.email}</option>
                                    <option value={"order"}>{strings.searchTypeSelector.order}</option>
                                </select>
                            </If>
                        </div>
                        <div className="form-group">
                            <input
                                type="text"
                                className="win-textbox"
                                style={{width: "100%"}}
                                placeholder={props.searchType === "email" 
                                    ? "john.doe@example.com"
                                    : "123456"}
                                value={props.searchTerm}
                                onChange={handleSetSearchTerm}
                            />
                        </div>
                    </div>
                    <MaybeError error={props.error} />
                    <div className="footer">
                        <div className="ms-row vc">
                            <div className="m-col-12-24">
                                <PoweredByFooter shopUrl={props.shopUrl} />
                            </div>
                            <div className="m-col-12-24 text-right">
                                <button className="btn btn-purple" type="button" onClick={beginSearch}>
                                    {strings.searchButton.beginSearch}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </section>
    );
}
